/**
 * 深度规范化为“顺序不敏感”的稳定字符串：
 * - 对象：按键名排序后规范化
 * - 数组：把每个元素规范化后整体排序（多重集合语义）
 * - Set：等价为无序数组处理
 * - Map：按 [keyStr, valStr] 组成条目后排序
 * - Date/RegExp/TypedArray 等作专门处理
 * 说明：不支持循环引用（检测到会抛错）
 */
function toCanonicalString(value, seen = new WeakSet(), keySet = null) {
  const t = typeof value
  if (value === null) return 'N:null'
  if (t === 'boolean') return `B:${value}`
  if (t === 'number') {
    if (Number.isNaN(value)) return 'Num:NaN'
    if (Object.is(value, -0)) return 'Num:-0'
    return `Num:${value}`
  }
  if (t === 'bigint') return `BigInt:${value.toString()}`
  if (t === 'string') return `S:${JSON.stringify(value)}`
  if (t === 'symbol') return `Sym:${String(value.description)}`
  if (t === 'undefined') return 'U:undefined'
  if (t === 'function') {
    return 'Fn:[function]'
  }

  if (seen.has(value)) {
    throw new TypeError('Unsupported circular reference detected.')
  }
  seen.add(value)

  if (value instanceof Date) {
    return `Date:${value.getTime()}`
  }
  if (value instanceof RegExp) {
    return `RegExp:${value.source}/${value.flags}`
  }
  if (ArrayBuffer.isView(value)) {
    const ctor = value.constructor && value.constructor.name || 'TypedArray'
    return `${ctor}:[${Array.from(value).join(',')}]`
  }
  if (value instanceof ArrayBuffer) {
    const view = new Uint8Array(value)
    return `ArrayBuffer:[${Array.from(view).join(',')}]`
  }

  if (value instanceof Set) {
    const parts = []
    for (const el of value) parts.push(toCanonicalString(el, seen, keySet))
    parts.sort()
    return `Set:[${parts.join(',')}]`
  }

  if (value instanceof Map) {
    const entries = []
    for (const [k, v] of value) {
      const ks = toCanonicalString(k, seen, keySet)
      const vs = toCanonicalString(v, seen, keySet)
      entries.push(`${ks}=>${vs}`)
    }
    entries.sort()
    return `Map:{${entries.join(',')}}`
  }

  if (Array.isArray(value)) {
    const items = value.map(v => toCanonicalString(v, seen, keySet))
    items.sort()
    return `Arr:[${items.join(',')}]`
  }

  const allKeys = Object.keys(value)
  const keys = keySet && keySet.size > 0
    ? allKeys.filter(k => keySet.has(k)).sort()
    : allKeys.sort()
  const kvPairs = keys.map(k => {
    const vs = toCanonicalString(value[k], seen, keySet)
    return `${JSON.stringify(k)}:${vs}`
  })
  const tag = value.constructor && value.constructor !== Object
    ? value.constructor.name
    : 'Object'
  return `${tag}:{${kvPairs.join(',')}}`
}

/**
 * 比较一个数组内的全部成员是否“深度相等（顺序不敏感）”
 * 规则：
 * - 0 个：false
 * - 1 个：true
 * - ≥2 个：逐一比较是否与首个成员等价
 */
export function allEqualUnordered(list, keys = []) {
  if (!Array.isArray(list)) {
    throw new TypeError('Expected an array as input.')
  }
  const n = list.length
  if (n === 0) return false
  if (n === 1) return true

  const keySet = Array.isArray(keys) ? new Set(keys) : new Set()

  let ref
  // eslint-disable-next-line no-useless-catch
  try {
    ref = toCanonicalString(list[0], new WeakSet(), keySet)
  } catch (e) {
    throw e
  }
  for (let i = 1; i < n; i++) {
    let cur
    // eslint-disable-next-line no-useless-catch
    try {
      cur = toCanonicalString(list[i], new WeakSet(), keySet)
    } catch (e) {
      throw e
    }
    if (cur !== ref) return false
  }
  return true
}

/**
 * 版本号比较函数
 * @param {string} version1 - 第一个版本号字符串
 * @param {string} version2 - 第二个版本号字符串
 * @param {string} operator - 比较操作符，支持 '>', '>=', '==', '<=', '<'
 * @returns {boolean} 根据操作符返回比较结果
 */
export function compareVersionSize(version1, version2, operator) {
  // 将版本号字符串转换为数字数组
  const v1 = version1.split('.').map(num => parseInt(num, 10))
  const v2 = version2.split('.').map(num => parseInt(num, 10))

  // 确保两个版本数组长度相同，较短的数组用0补齐
  while (v1.length < v2.length) v1.push(0)
  while (v2.length < v1.length) v2.push(0)

  // 比较版本号
  for (let i = 0; i < v1.length; i++) {
    if (v1[i] < v2[i]) {
      return operator === '<' || operator === '<='
    }
    if (v1[i] > v2[i]) {
      return operator === '>' || operator === '>='
    }
  }

  // 如果所有部分相同，返回根据符号判断是否相等
  return operator === '==' || operator === '<=' || operator === '>='
}

let _tGeocoderInstance

export function geocodeByTianditu(address) {
  return new Promise((resolve) => {
    const keyword = address === null || address === undefined ? '' : String(address).trim()
    if (!keyword) return resolve(null)

    const T = window.T
    if (!T || typeof T.Geocoder !== 'function') return resolve(null)

    if (!_tGeocoderInstance) {
      try {
        _tGeocoderInstance = new T.Geocoder()
      } catch (e) {
        void e
        return resolve(null)
      }
    }

    try {
      _tGeocoderInstance.getPoint(keyword, (result) => {
        if (!result || typeof result.getStatus !== 'function') return resolve(null)
        if (result.getStatus() !== 0) return resolve(null)
        if (typeof result.getLocationPoint !== 'function') return resolve(null)

        const point = result.getLocationPoint()
        if (!point) return resolve(null)

        const lng = typeof point.getLng === 'function' ? point.getLng() : point.lng
        const lat = typeof point.getLat === 'function' ? point.getLat() : point.lat
        if (!Number.isFinite(Number(lng)) || !Number.isFinite(Number(lat))) return resolve(null)

        resolve({
          lng: Number(lng).toFixed(6),
          lat: Number(lat).toFixed(6)
        })
      })
    } catch (e) {
      void e
      resolve(null)
    }
  })
}

/**
 * Parse the time to string
 * @param {(Object|string|number)} time
 * @param {string} cFormat
 * @returns {string}
 */
export function parseTime(time, cFormat) {
  if (time === undefined || time === null || time === '') {
    return null
  }
  if (arguments.length === 0) {
    return null
  }
  const format = cFormat || '{y}-{m}-{d} {h}:{i}:{s}'
  let date
  if (typeof time === 'object') {
    date = time
  } else {
    if ((typeof time === 'string') && (/^[0-9]+$/.test(time))) {
      time = parseInt(time)
    }
    if ((typeof time === 'number') && (time.toString().length === 10)) {
      time = time * 1000
    }
    date = new Date(time)
  }
  const formatObj = {
    y: date.getFullYear(),
    m: date.getMonth() + 1,
    d: date.getDate(),
    h: date.getHours(),
    i: date.getMinutes(),
    s: date.getSeconds(),
    a: date.getDay()
  }
  const time_str = format.replace(/{(y|m|d|h|i|s|a)+}/g, (result, key) => {
    let value = formatObj[key]
    // Note: getDay() returns 0 on Sunday
    if (key === 'a') { return ['日', '一', '二', '三', '四', '五', '六'][value] }
    if (result.length > 0 && value < 10) {
      value = '0' + value
    }
    return value || 0
  })
  return time_str
}

/**
 * @param {number} time
 * @param {string} option
 * @returns {string}
 */
export function formatTime(time, option) {
  if (('' + time).length === 10) {
    time = parseInt(time) * 1000
  } else {
    time = +time
  }
  const d = new Date(time)
  const now = Date.now()

  const diff = (now - d) / 1000

  if (diff < 30) {
    return '刚刚'
  } else if (diff < 3600) {
    // less 1 hour
    return Math.ceil(diff / 60) + '分钟前'
  } else if (diff < 3600 * 24) {
    return Math.ceil(diff / 3600) + '小时前'
  } else if (diff < 3600 * 24 * 2) {
    return '1天前'
  }
  if (option) {
    return parseTime(time, option)
  } else {
    return (
      d.getMonth() +
      1 +
      '月' +
      d.getDate() +
      '日' +
      d.getHours() +
      '时' +
      d.getMinutes() +
      '分'
    )
  }
}

/**
 * @param {string} url
 * @returns {Object}
 */
export function getQueryObject(url) {
  url = url == null ? window.location.href : url
  const search = url.substring(url.lastIndexOf('?') + 1)
  const obj = {}
  const reg = /([^?&=]+)=([^?&=]*)/g
  search.replace(reg, (rs, $1, $2) => {
    const name = decodeURIComponent($1)
    let val = decodeURIComponent($2)
    val = String(val)
    obj[name] = val
    return rs
  })
  return obj
}

/**
 * @param {string} input value
 * @returns {number} output value
 */
export function byteLength(str) {
  // returns the byte length of an utf8 string
  let s = str.length
  for (var i = str.length - 1; i >= 0; i--) {
    const code = str.charCodeAt(i)
    if (code > 0x7f && code <= 0x7ff) s++
    else if (code > 0x7ff && code <= 0xffff) s += 2
    if (code >= 0xDC00 && code <= 0xDFFF) i--
  }
  return s
}

/**
 * @param {Array} actual
 * @returns {Array}
 */
export function cleanArray(actual) {
  const newArray = []
  for (let i = 0; i < actual.length; i++) {
    if (actual[i]) {
      newArray.push(actual[i])
    }
  }
  return newArray
}

/**
 * @param {Object} json
 * @returns {Array}
 */
export function param(json) {
  if (!json) return ''
  return cleanArray(
    Object.keys(json).map(key => {
      if (json[key] === undefined) return ''
      return encodeURIComponent(key) + '=' + encodeURIComponent(json[key])
    })
  ).join('&')
}

/**
 * @param {string} url
 * @returns {Object}
 */
export function param2Obj(url) {
  const search = url.split('?')[1]
  if (!search) {
    return {}
  }
  return JSON.parse(
    '{"' +
    decodeURIComponent(search)
      .replace(/"/g, '\\"')
      .replace(/&/g, '","')
      .replace(/=/g, '":"')
      .replace(/\+/g, ' ') +
    '"}'
  )
}

/**
 * @param {string} val
 * @returns {string}
 */
export function html2Text(val) {
  const div = document.createElement('div')
  div.innerHTML = val
  return div.textContent || div.innerText
}

/**
 * Merges two objects, giving the last one precedence
 * @param {Object} target
 * @param {(Object|Array)} source
 * @returns {Object}
 */
export function objectMerge(target, source) {
  if (typeof target !== 'object') {
    target = {}
  }
  if (Array.isArray(source)) {
    return source.slice()
  }
  Object.keys(source).forEach(property => {
    const sourceProperty = source[property]
    if (typeof sourceProperty === 'object') {
      target[property] = objectMerge(target[property], sourceProperty)
    } else {
      target[property] = sourceProperty
    }
  })
  return target
}

/**
 * @param {HTMLElement} element
 * @param {string} className
 */
export function toggleClass(element, className) {
  if (!element || !className) {
    return
  }
  let classString = element.className
  const nameIndex = classString.indexOf(className)
  if (nameIndex === -1) {
    classString += '' + className
  } else {
    classString =
      classString.substr(0, nameIndex) +
      classString.substr(nameIndex + className.length)
  }
  element.className = classString
}

/**
 * @param {string} type
 * @returns {Date}
 */
export function getTime(type) {
  if (type === 'start') {
    return new Date().getTime() - 3600 * 1000 * 24 * 90
  } else {
    return new Date(new Date().toDateString())
  }
}

/**
 * @param {Function} func
 * @param {number} wait
 * @param {boolean} immediate
 * @return {*}
 */
export function debounce(func, wait, immediate) {
  let timeout, args, context, timestamp, result

  const later = function() {
    // 据上一次触发时间间隔
    const last = +new Date() - timestamp

    // 上次被包装函数被调用时间间隔 last 小于设定时间间隔 wait
    if (last < wait && last > 0) {
      timeout = setTimeout(later, wait - last)
    } else {
      timeout = null
      // 如果设定为immediate===true，因为开始边界已经调用过了此处无需调用
      if (!immediate) {
        result = func.apply(context, args)
        if (!timeout) context = args = null
      }
    }
  }

  return function(...args) {
    context = this
    timestamp = +new Date()
    const callNow = immediate && !timeout
    // 如果延时不存在，重新设定延时
    if (!timeout) timeout = setTimeout(later, wait)
    if (callNow) {
      result = func.apply(context, args)
      context = args = null
    }

    return result
  }
}

/**
 * This is just a simple version of deep copy
 * Has a lot of edge cases bug
 * If you want to use a perfect deep copy, use lodash's _.cloneDeep
 * @param {Object} source
 * @returns {Object}
 */
export function deepClone(source) {
  if (!source && typeof source !== 'object') {
    throw new Error('error arguments', 'deepClone')
  }
  const targetObj = source.constructor === Array ? [] : {}
  Object.keys(source).forEach(keys => {
    if (source[keys] && typeof source[keys] === 'object') {
      targetObj[keys] = deepClone(source[keys])
    } else {
      targetObj[keys] = source[keys]
    }
  })
  return targetObj
}

/**
 * @param {Array} arr
 * @returns {Array}
 */
export function uniqueArr(arr) {
  return Array.from(new Set(arr))
}

/**
 * @returns {string}
 */
export function createUniqueString() {
  const timestamp = +new Date() + ''
  const randomNum = parseInt((1 + Math.random()) * 65536) + ''
  return (+(randomNum + timestamp)).toString(32)
}

/**
 * Check if an element has a class
 * @param {HTMLElement} elm
 * @param {string} cls
 * @returns {boolean}
 */
export function hasClass(ele, cls) {
  return !!ele.className.match(new RegExp('(\\s|^)' + cls + '(\\s|$)'))
}

/**
 * Add class to element
 * @param {HTMLElement} elm
 * @param {string} cls
 */
export function addClass(ele, cls) {
  if (!hasClass(ele, cls)) ele.className += ' ' + cls
}

/**
 * Remove class from element
 * @param {HTMLElement} elm
 * @param {string} cls
 */
export function removeClass(ele, cls) {
  if (hasClass(ele, cls)) {
    const reg = new RegExp('(\\s|^)' + cls + '(\\s|$)')
    ele.className = ele.className.replace(reg, ' ')
  }
}
// 时间戳转换为时间
export function dateFilter(data, format = '') {
  if (!data) {
    return '--'
  }
  var dt = new Date(data)
  var y = dt.getFullYear()
  var m = (dt.getMonth() + 1).toString().padStart(2, '0')
  var d = dt.getDate().toString().padStart(2, '0')
  var h = dt.getHours().toString().padStart(2, '0')
  var mm = dt.getMinutes().toString().padStart(2, '0')
  var s = dt.getSeconds().toString().padStart(2, '0')
  if (
    format.toLocaleLowerCase() === 'yyyy-mm-dd' ||
    format.toLocaleLowerCase() === ''
  ) {
    return `${y}-${m}-${d}`
  } else if (format.toLocaleLowerCase() === 'yyyy/mm/dd') {
    return `${y}/${m}/${d}`
  } else if (format.toLocaleLowerCase() === 'yyyy-mm-dd hh:mm:ss') {
    return `${y}-${m}-${d} ${h}:${mm}:${s}`
  } else if (format.toLocaleLowerCase() === 'yyyy/mm/dd hh:mm:ss') {
    return `${y}/${m}/${d} ${h}:${mm}:${s}`
  } else if (format.toLocaleLowerCase() === 'hh:mm:ss') {
    return ` ${h}:${mm}`
  } else {
    return `输入的时间格式有误`
  }
}
